import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Stepper,
  STEPPER_DEMO_STEPS,
  STEPPER_LIVE_STEPS,
  STEPPER_PREVIEW_STEP_LABELS,
  STEPPER_PREVIEW_STEPS,
  type StepperOrientation,
  type StepperPreviewStep,
  type StepperStepStatus,
} from '@components/ui'
import { ReactComponent as CheckmarkIcon } from '@assets/images/Icons/Checkmark.svg'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './StepperShowcase.css'

const STEP_STATUS_LABELS: Record<StepperStepStatus, string> = {
  inactive: 'Inactive',
  active: 'Active',
  completed: 'Completed',
}

const STEP_STATUSES: StepperStepStatus[] = ['inactive', 'active', 'completed']

const StepperStepPreview: React.FC<{
  status: StepperStepStatus
  label?: string
  darkMode?: boolean
}> = ({ status, label = 'Text', darkMode = false }) => (
  <div
    className={`ds-stepper-step-preview ds-stepper-step-preview--${status}${
      darkMode ? ' ds-stepper-step-preview--dark' : ''
    }`}
  >
    <div className="ds-stepper-step-preview__indicator" aria-hidden="true">
      {status === 'completed' ? (
        <CheckmarkIcon
          className="ds-stepper-step-preview__check"
          width={24}
          height={24}
          aria-hidden="true"
        />
      ) : (
        <span className="ds-stepper-step-preview__number">1</span>
      )}
    </div>
    <span className="ds-stepper-step-preview__label">{label}</span>
  </div>
)

const ORIENTATION_LABELS: Record<StepperOrientation, string> = {
  horizontal: 'Horizontal',
  vertical: 'Vertical',
}

const StepperShowcase: React.FC = () => {
  const [orientation, setOrientation] =
    useState<StepperOrientation>('horizontal')
  const [highlightedStep, setHighlightedStep] = useState<StepperPreviewStep>(1)
  const [darkMode, setDarkMode] = useState(false)
  const rowRefs = useRef<
    Partial<Record<StepperPreviewStep, HTMLDivElement | null>>
  >({})
  const skipInitialScroll = useRef(true)
  const isVertical = orientation === 'vertical'
  const currentStep = highlightedStep - 1

  useEffect(() => {
    if (skipInitialScroll.current) {
      skipInitialScroll.current = false
      return
    }

    rowRefs.current[highlightedStep]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedStep])

  const handleStepChange = (stepIndex: number): void => {
    setHighlightedStep((stepIndex + 1) as StepperPreviewStep)
  }

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'stepper-orientation',
            label: 'Orientation',
            control: (
              <select
                id="stepper-orientation"
                className="ui-field-control"
                value={orientation}
                onChange={(event) =>
                  setOrientation(event.target.value as StepperOrientation)
                }
              >
                {(Object.keys(ORIENTATION_LABELS) as StepperOrientation[]).map(
                  (option) => (
                    <option key={option} value={option}>
                      {ORIENTATION_LABELS[option]}
                    </option>
                  ),
                )}
              </select>
            ),
          },
          {
            id: 'stepper-dark-mode',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="stepper-dark-mode"
                  type="checkbox"
                  checked={darkMode}
                  onChange={(event) => setDarkMode(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'Progress',
        fields: [
          {
            id: 'stepper-highlight-step',
            label: 'Highlighted step',
            control: (
              <select
                id="stepper-highlight-step"
                className="ui-field-control"
                value={highlightedStep}
                onChange={(event) =>
                  setHighlightedStep(
                    Number(event.target.value) as StepperPreviewStep,
                  )
                }
              >
                {STEPPER_PREVIEW_STEPS.map((step) => (
                  <option key={step} value={step}>
                    {STEPPER_PREVIEW_STEP_LABELS[step]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
    ],
    [darkMode, highlightedStep, orientation],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Stepper"
        description="40px nodes, 4px connectors, inactive / active / completed."
        stageClassName="ui-preview__stage--wide stepper-showcase__stage-wrap"
      >
        <div
          className={`stepper-showcase__sheet${
            darkMode ? ' stepper-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              darkMode ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              {isVertical
                ? 'Vertical placement beside the form. Click a step label to jump between steps.'
                : 'Horizontal placement above the form. Click a step label to jump between steps.'}
            </p>
            <div
              className={`stepper-showcase__form-shell${
                isVertical ? ' stepper-showcase__form-shell--vertical' : ''
              }${darkMode ? ' stepper-showcase__form-shell--dark' : ''}`}
            >
              <Stepper
                orientation={orientation}
                steps={STEPPER_LIVE_STEPS}
                currentStep={currentStep}
                darkMode={darkMode}
                onStepClick={handleStepChange}
              />
              <div className="stepper-showcase__form-body">
                <div className="stepper-showcase__form-field" />
                <div className="stepper-showcase__form-field" />
                <div className="stepper-showcase__form-field stepper-showcase__form-field--short" />
              </div>
            </div>
          </section>

          <section className="stepper-showcase__matrix">
            <h4 className="stepper-showcase__section-title">Progress states</h4>
            {STEPPER_PREVIEW_STEPS.map((previewStep) => (
              <div
                key={previewStep}
                ref={(element) => {
                  rowRefs.current[previewStep] = element
                }}
                className={`stepper-showcase__row${
                  previewStep === highlightedStep
                    ? ' stepper-showcase__row--selected'
                    : ''
                }${
                  isVertical ? ' stepper-showcase__row--vertical-preview' : ''
                }${darkMode ? ' stepper-showcase__row--dark' : ''}`}
              >
                <span className="stepper-showcase__row-label">
                  {STEPPER_PREVIEW_STEP_LABELS[previewStep]}
                </span>
                <Stepper
                  orientation={orientation}
                  steps={STEPPER_DEMO_STEPS}
                  previewStep={previewStep}
                  darkMode={darkMode}
                />
              </div>
            ))}
          </section>

          <section className="stepper-showcase__matrix">
            <h4 className="stepper-showcase__section-title">Step states</h4>
            <div className="stepper-showcase__step-states">
              {STEP_STATUSES.map((status) => (
                <div
                  key={status}
                  className={`stepper-showcase__step-state-row${
                    darkMode ? ' stepper-showcase__step-state-row--dark' : ''
                  }`}
                >
                  <span className="stepper-showcase__row-label">
                    {STEP_STATUS_LABELS[status]}
                  </span>
                  <StepperStepPreview status={status} darkMode={darkMode} />
                </div>
              ))}
            </div>
          </section>
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Stepper" sections={propertySections} />
    </>
  )
}

export default StepperShowcase
